import i18n from "@/i18n/config";
import { apiClient } from "@/services/apiClient";
import { readMagDistinctId, readStoredUtms } from "@/lib/mag-analytics";

export type CertificateCheckoutFlow = "notary" | "accountant";

export type CertificateCheckoutLocale = "es" | "en";

function resolveCheckoutLocale(): CertificateCheckoutLocale {
  const lng = (i18n.language ?? "es").toLowerCase();
  return lng.startsWith("en") ? "en" : "es";
}

export type CertificateCheckoutPaymentMethod = "dlocal" | "bank_transfer";

/** What the consumer needs in order to make the transfer from their own bank. */
export type BankTransferInstructions = {
  bankShortName: string;
  bankOfficialName: string;
  accountHolder: string;
  accountNumber: string;
  accountType: string;
  currency: string;
  reference: string;
};

export type CertificateCheckoutCreateResponse = {
  checkoutId: string;
  redirectUrl: string | null;
  status: "pending" | "paid";
  amountMinor: number;
  currency: string;
  /**
   * What this checkout is actually paid with, RESOLVED SERVER-SIDE.
   *
   * We ask for a card or a transfer; which card provider serves that is a
   * deployment decision we are told about, not one we make. It is what says
   * whether to follow `redirectUrl` ("dlocal"), render the Payment Brick
   * ("mercadopago") or show transfer instructions ("bank_transfer") — a null
   * `redirectUrl` alone does not distinguish the last two.
   */
  paymentMethod: "dlocal" | "mercadopago" | "bank_transfer";
  /** Present only for paymentMethod="bank_transfer"; there is no redirectUrl in that case. */
  bankTransfer?: BankTransferInstructions;
};

/**
 * Payment Brick's INNER `formData`, forwarded as-is (spec c086002b, D5).
 *
 * The Brick's `onSubmit` receives `{ selectedPaymentMethod, formData }` and
 * this is the INNER half. Sending the outer object makes Mercado Pago answer
 * `required_properties` — measured, and it reads like a bug on our side.
 *
 * `transaction_amount` is deliberately NOT forwarded even though the Brick
 * sends it: the server re-derives the amount from the checkout row and strips
 * any amount it is sent, so passing it on would be noise that looks like it
 * matters.
 */
export type MercadoPagoBrickFormData = {
  token: string;
  payment_method_id: string;
  installments?: number;
  payer: {
    email: string;
    identification?: { type: string; number: string };
  };
};

export type MercadoPagoPaymentResponse = {
  checkoutId: string;
  /**
   * "failed" is RETRYABLE: the checkout stays pending server-side so the
   * consumer can try another card. It does not mean the checkout is dead.
   */
  status: "paid" | "pending" | "failed";
  providerPaymentId: string | null;
};

/** Create the Mercado Pago order for a pending checkout from the Brick's token. */
export function payCertificateCheckoutWithMercadoPago(
  checkoutId: string,
  formData: MercadoPagoBrickFormData,
): Promise<MercadoPagoPaymentResponse> {
  return apiClient
    .post<MercadoPagoPaymentResponse>(
      `/me/certificate-payments/${checkoutId}/mercadopago-order`,
      {
        token: formData.token,
        payment_method_id: formData.payment_method_id,
        ...(formData.installments != null ? { installments: formData.installments } : {}),
        payer: {
          email: formData.payer.email,
          ...(formData.payer.identification?.number
            ? { identification: formData.payer.identification }
            : {}),
        },
      },
    )
    .then((res) => res.data);
}

export type CertificateCheckoutStatusResponse = {
  checkoutId: string;
  status: string;
  amountMinor: number;
  currency: string;
  flow: CertificateCheckoutFlow;
  targetType: string;
  paidAt: string | null;
  /** "quote" for an RFQ checkout, "catalog" for a priced product. */
  pricingSource?: string;
  /** Set for a quote checkout: the RFQ this paid for. */
  quoteRequestId?: string | null;
  /** The spawned request, once it exists — null in the window right after payment. */
  childRequestId?: string | null;
  childRequestType?: string | null;
  /** "dlocal" | "bank_transfer" — a transfer checkout has no redirect to follow. */
  paymentMethod?: string;
  /** Non-null once the comprobante is uploaded; the request may be created from here. */
  transferSubmittedAt?: string | null;
  /** "pending" | "verified" | "flagged" while the transfer is being checked. */
  transferVerification?: string | null;
  transferReference?: string | null;
  /**
   * The charge itemised, as the SERVER built it — the same lines the payment
   * provider's order carries, summing to amountMinor exactly.
   *
   * Not assembled client-side on purpose: the payment page is reached by
   * checkout id alone, with no quote in hand, and a second implementation of
   * the breakdown is how the composer and the charge drifted apart before.
   */
  items?: Array<{ title: string; amountMinor: number }>;
  /**
   * Which provider this deployment takes cards with — distinct from
   * `paymentMethod`, which is what this checkout IS. A page offering to switch
   * back to a card needs to know where that would land before offering it.
   */
  cardProvider?: string;
  /** What this checkout's factura will say, as chosen so far. */
  invoice?: {
    includeRut: boolean;
    rutDigits: string | null;
    legalName: string | null;
    address: string | null;
  };
  /** The consumer's remembered fiscal identity, for prefilling. Never auto-applied. */
  invoiceSuggestion?: { rutDigits: string; legalName: string; address: string } | null;
};

/**
 * Record what the consumer wants their factura to say.
 *
 * Razón social and address are REQUIRED alongside a RUT — Art. 124 Decreto
 * 220/998 requires the buyer identified by name and RUT on an e-Factura — and
 * the server refuses the combination without them.
 */
export function setCheckoutInvoiceDetails(
  checkoutId: string,
  input: {
    includeRut: boolean;
    rutDigits?: string;
    legalName?: string;
    address?: string;
    rememberForFuture?: boolean;
  },
): Promise<{ ok: true }> {
  return apiClient
    .post<{ ok: true }>(`/me/certificate-payments/${checkoutId}/invoice`, input)
    .then((res) => res.data);
}

export function createCertificateCheckout(
  flow: CertificateCheckoutFlow,
  targetType: string,
  billableFileCount?: number,
  fulfillmentMode?: string,
  paymentMethod: CertificateCheckoutPaymentMethod = "dlocal",
): Promise<CertificateCheckoutCreateResponse> {
  // Visitor's Magig browser distinct id, so the server-fired request_paid
  // conversion stitches to the on-site funnel. Null before mag assigns an id
  // (brand-new session) — omit the key entirely in that case; never block.
  const magDistinctId = readMagDistinctId();
  return apiClient
    .post<CertificateCheckoutCreateResponse>("/me/certificate-payments/checkout", {
      flow,
      targetType,
      ...(billableFileCount != null && billableFileCount > 0 ? { billableFileCount } : {}),
      ...(fulfillmentMode != null && fulfillmentMode !== "" ? { fulfillmentMode } : {}),
      locale: resolveCheckoutLocale(),
      // Forward captured UTM attribution so the server-fired request_paid event
      // can carry the marketing source that brought the visitor in. Only keys
      // with a stored value are included; empty/missing keys are omitted.
      ...readStoredUtms(),
      ...(magDistinctId ? { mag_distinct_id: magDistinctId } : {}),
      paymentMethod,
    })
    .then((res) => res.data);
}

/**
 * Upload the comprobante for a bank-transfer checkout.
 *
 * Resolving does NOT mean the payment is confirmed — it means the receipt is
 * stored and the request can now be created. Verification runs server-side
 * afterwards and may still route to a human.
 */
export type BankTransferAccountResponse = {
  configured: boolean;
  bankShortName: string | null;
  bankOfficialName: string | null;
  accountHolder: string | null;
  accountNumber: string | null;
  accountType: string | null;
  currency: string | null;
};

/**
 * The account to transfer to. Fetched rather than carried through navigation:
 * the hand-off to the instructions screen is a full page load, and the
 * consumer may refresh it or come back to it while in their banking app.
 */
export function getBankTransferAccount(): Promise<BankTransferAccountResponse> {
  return apiClient
    .get<BankTransferAccountResponse>("/me/certificate-payments/bank-transfer-account")
    .then((res) => res.data);
}

export function uploadTransferReceipt(
  checkoutId: string,
  file: File,
): Promise<{ ok: true; verification: "pending" }> {
  const form = new FormData();
  form.append("receipt", file);
  return apiClient
    .post<{ ok: true; verification: "pending" }>(
      `/me/certificate-payments/${checkoutId}/receipt`,
      form,
    )
    .then((res) => res.data);
}

/**
 * Switch a pending card checkout to bank transfer.
 *
 * Refused by the server once any provider payment exists: a card payment in
 * flight and a bank transfer owed are not two views of one debt.
 */
export function switchCheckoutToBankTransfer(
  checkoutId: string,
): Promise<{ ok: true; transferReference: string }> {
  return apiClient
    .post<{ ok: true; transferReference: string }>(
      `/me/certificate-payments/${checkoutId}/bank-transfer`,
    )
    .then((res) => res.data);
}

/**
 * Switch a transfer checkout back to the card provider.
 *
 * Refused once a receipt is uploaded: a transfer under human reconciliation
 * plus a card payment beside it is one job paid twice.
 */
export function switchCheckoutToCard(
  checkoutId: string,
): Promise<{ ok: true; paymentMethod: string }> {
  return apiClient
    .post<{ ok: true; paymentMethod: string }>(
      `/me/certificate-payments/${checkoutId}/card`,
    )
    .then((res) => res.data);
}

export function getCertificateCheckout(checkoutId: string): Promise<CertificateCheckoutStatusResponse> {
  return apiClient
    .get<CertificateCheckoutStatusResponse>(`/me/certificate-payments/${checkoutId}`)
    .then((res) => res.data);
}

export type ResumableCheckout = {
  checkoutId: string;
  flow: CertificateCheckoutFlow;
  targetType: string;
  amountMinor: number;
  currency: string;
  paidAt: string | null;
};

/**
 * Paid checkouts that never became a request — the post-payment resume was
 * interrupted (lost session, closed tab, profile-gate hang). Used by the
 * requests-list banner to offer a one-tap "finish your paid request" path.
 */
export function listResumableCheckouts(): Promise<ResumableCheckout[]> {
  return apiClient
    .get<ResumableCheckout[]>("/me/certificate-payments/resumable")
    .then((res) => res.data);
}
